<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { format, parseISO } from 'date-fns';
import DetailRow from './DetailRow.vue';

const props = defineProps({
  details: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const KEY = 'CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FORM.HOTEL';

const board = computed(() =>
  props.details.boardType
    ? t(`${KEY}.BOARD_TYPE_OPTIONS.${props.details.boardType.toUpperCase()}`)
    : ''
);

// Naive date strings have no timezone; parseISO treats them as local, so the
// original digits survive without a UTC shift.
const formatDate = value => {
  if (!value) return '';
  try {
    return format(parseISO(value), 'dd MMM yyyy');
  } catch {
    return value;
  }
};

const stay = computed(() =>
  [props.details.checkIn, props.details.checkOut]
    .filter(Boolean)
    .map(formatDate)
    .join(' → ')
);

const nights = computed(() =>
  props.details.nights
    ? t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.NIGHTS', {
        count: props.details.nights,
      })
    : ''
);

const guests = computed(() => props.details.guests || {});

const guestCount = computed(() =>
  [
    guests.value.adults &&
      `${guests.value.adults} ${t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.ADULTS')}`,
    guests.value.children &&
      `${guests.value.children} ${t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.CHILDREN')}`,
  ]
    .filter(Boolean)
    .join(', ')
);

const names = computed(() => (guests.value.names || []).filter(Boolean));
</script>

<template>
  <div class="space-y-2">
    <p class="text-xs font-medium text-n-slate-11">
      {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.HOTEL') }}
    </p>
    <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-3 space-y-2">
      <DetailRow :label="t(`${KEY}.NAME`)" :value="details.hotelName" />
      <DetailRow :label="t(`${KEY}.CITY`)" :value="details.city" />
      <DetailRow
        :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.STAY')"
        :value="stay"
      />
      <DetailRow
        :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.DURATION')"
        :value="nights"
      />
      <DetailRow :label="t(`${KEY}.ROOM_TYPE`)" :value="details.roomType" />
      <DetailRow
        :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.ROOMS')"
        :value="details.rooms"
      />
      <DetailRow :label="t(`${KEY}.BOARD_TYPE`)" :value="board" />
      <DetailRow
        :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.GUESTS')"
        :value="guestCount"
      />
      <DetailRow :label="t(`${KEY}.GUEST_NAMES`)" :value="names.join(', ')" />
      <DetailRow
        :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.NOTES')"
        :value="details.notes"
      />
    </div>
  </div>
</template>
